import { Delete } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { cloneDeep, get, isArray, set } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import MuiDialog from 'src/components/ui-component/mui-dialog/MuiDialog';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import { APIS } from 'src/lib/constants/apis';
import { enumT } from 'src/utils/helper';

// import schemas
import { KOSTEN_ARTEN } from 'src/lib/constants/constant';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const PositionLiegtNichtVor: FC<{ kostenPosition: string; kostenArt: string; updatedReadOnly: boolean }> = ({
	kostenArt,
	kostenPosition,
	updatedReadOnly,
}) => {
	const { application, saveApplication } = useApplication();
	const [showPopUp, setShowPopUp] = useState(false);

	const isVerwaltungskosten = kostenArt === KOSTEN_ARTEN.Verwaltungskostenpauschale;
	const isReisekosten = kostenArt === KOSTEN_ARTEN.Reisekosten;
	const VERWALTUNGSKOSTEN = 'Verwaltungskosten';
	const getButtonText = () => {
		switch (kostenArt) {
			case KOSTEN_ARTEN.Personalkosten:
			case KOSTEN_ARTEN.Sachkosten:
				return `Es werden keine ${enumT(kostenArt)} beantragt.`;
			case KOSTEN_ARTEN.Verwaltungskostenpauschale:
				return `Es werden keine ${VERWALTUNGSKOSTEN} beantragt.`;
			default:
				return `Alle ${enumT(kostenArt)} löschen`;
		}
	};

	const getButtonTitle = () => {
		switch (kostenArt) {
			case KOSTEN_ARTEN.Personalkosten:
			case KOSTEN_ARTEN.Sachkosten:
				return `${enumT(kostenArt)}-Erfassung löschen`;
			case KOSTEN_ARTEN.Verwaltungskostenpauschale:
				return `${VERWALTUNGSKOSTEN}-Erfassung löschen`;
			default:
				return `Alle ${enumT(kostenArt)} löschen`;
		}
	};

	const RenderKostenButton = () => {
		return (
			<Button
				variant='outlined'
				color={isReisekosten ? 'error' : 'primary'}
				disabled={updatedReadOnly}
				onClick={() => {
					setShowPopUp(true);
				}}
				endIcon={<Delete />}
				sx={{
					maxWidth: 'fit-content',
					backgroundColor: isReisekosten ? '' : '#E5F6FD',
				}}
			>
				{getButtonText()}
			</Button>
		);
	};
	const getContentTitle = () => {
		switch (kostenArt) {
			case KOSTEN_ARTEN.Personalkosten:
			case KOSTEN_ARTEN.Sachkosten:
				return `Sind Sie sicher, dass Sie keine ${enumT(kostenArt)} beantragen und den Bereich "${enumT(kostenArt)}-Erfassung" für diesen Antrag löschen möchten?`;
			case KOSTEN_ARTEN.Verwaltungskostenpauschale:
				return `Sind Sie sicher, dass Sie keine ${enumT(kostenArt)} beantragen und den Bereich "${VERWALTUNGSKOSTEN}-Erfassung" für diesen Antrag löschen möchten?`;
			default:
				return `Sind Sie sicher, dass Sie alle löschen möchten?`;
		}
	};

	const anzeigeText = useMemo(() => {
		if (kostenArt === KOSTEN_ARTEN.Verwaltungskostenpauschale) {
			return (
				<>
					<Typography variant='body2'>In Ihrem beantragten Vorhaben sollen keine Verwaltungskosten abgerechnet werden?</Typography>
					<Typography variant='body2' sx={{ mb: 2 }}>
						Dann klicken Sie auf den Button unter diesem Hinweis, um die {VERWALTUNGSKOSTEN}-Erfassung zu überspringen.
					</Typography>
					<RenderKostenButton />
				</>
			);
		} else if (kostenArt === KOSTEN_ARTEN.Reisekosten) {
			return (
				<>
					<Typography variant='body2'>In Ihrem beantragten Vorhaben gibt es keine {enumT(kostenArt)}?</Typography>
					<Typography variant='body2' sx={{ mb: 2 }}>
						Dann klicken Sie auf den unteren Button, um alle {enumT(kostenArt)} zu löschen.
					</Typography>
					<RenderKostenButton />
				</>
			);
		} else {
			return (
				<>
					<Typography variant='body2'>In Ihrem beantragten Vorhaben gibt es keine {enumT(kostenArt)}?</Typography>
					<Typography variant='body2' sx={{ mb: 2 }}>
						Dann klicken Sie auf den Button unter diesem Hinweis, um die {enumT(kostenArt)}-Erfassung zu überspringen.
					</Typography>
					<RenderKostenButton />
				</>
			);
		}
	}, [isVerwaltungskosten, kostenArt]);

	const handleDeletePosition = () => {
		const appCopy = cloneDeep(application);
		try {
			const allKostenpositionen = get(application, P.Foerderantrag_Kostenpositions.path, []);
			const allFilteredPositionToDelete = allKostenpositionen.filter((eachKP: any) => {
				return (
					get(eachKP, KP_P.Allgemein_Kostenposition.path) === kostenPosition &&
					get(eachKP, KP_P.Allgemein_Kostenart_Finanzierung.path) === kostenArt
				);
			});

			allFilteredPositionToDelete.forEach(async (eachPosition: any) => {
				const positionID = Number(get(eachPosition, 'id', null));
				if (positionID > 0 && !updatedReadOnly) {
					await DeleteRecord(APIS.Kostenposition, positionID);
				}
			});

			const remainingKostenpositionen = allKostenpositionen.filter((eachKP: any) => {
				return get(eachKP, KP_P.Allgemein_Kostenart_Finanzierung.path) !== kostenArt;
			});

			if (isArray(remainingKostenpositionen) && remainingKostenpositionen.length > 0) {
				set(appCopy, P.Foerderantrag_Kostenpositions.path, remainingKostenpositionen);
			} else {
				set(appCopy, P.Foerderantrag_Kostenpositions.path, []);
			}
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('Error deleting positions: ', err);
		} finally {
			setShowPopUp(false);
		}
	};

	return (
		<>
			<MuiDialog
				open={showPopUp}
				onClose={() => setShowPopUp(false)}
				title={getButtonTitle()}
				content={getContentTitle()}
				onConfirm={() => {
					handleDeletePosition();
				}}
				onConfirmButtonName='Löschen'
			/>
			<Stack spacing={2} marginBottom={2}>
				<MUIAlert infoType={'info'} message={anzeigeText} />
			</Stack>
		</>
	);
};

export default PositionLiegtNichtVor;
